import { useState } from "react";
import { T, STATE, canAccess, getStateConfig } from "../../config/theme.js";
import { Card, Btn, SectionTitle } from "../ui/index.jsx";
import { Plus, Minus, Map, ChevronRight, ArrowRight, Target, CheckCircle2, HelpCircle, Bell, Lock, HeartPulse } from "lucide-react";
import AlertSetter from "./AlertSetter.jsx";

export default function DetailOverview({ holding, report, m, p, plan, onUpgrade, token, anxietyLevel, onAction }) {
  // 상태별 Lucide 아이콘 매핑 (Sprint 20: 새 라벨 + 기존 fallback)
  const stateIconMap = {
    STABLE: CheckCircle2,
    CHANGE: HelpCircle,
    VOLATILE: Target,
    CHECKNEEDED: Lock,
    // 하위호환
    HOLD: CheckCircle2,
    WATCH: HelpCircle,
    CAUTION: Target,
    PAUSE: Lock,
  };
  const StateIcon = stateIconMap[report?.state_stock] || HelpCircle;
  const sc = getStateConfig(report?.state_stock);

  return (
    <div>
      {/* ── 상태 요약 카드 ── */}
      <Card style={{ marginBottom: 20, borderLeft: `4px solid ${sc.color}`, background: `linear-gradient(135deg, ${T.card}, ${sc.color}05)` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ padding: 6, background: sc.color + "20", borderRadius: 8 }}>
            <StateIcon size={20} color={sc.color} strokeWidth={2} />
          </div>
          <div style={{ fontSize: 15, color: sc.color, fontWeight: 700 }}>{sc.desc}</div>
        </div>
        <div style={{ fontSize: 14, color: T.text, lineHeight: 1.7 }}>{report.summary}</div>
      </Card>

      {/* ── 매매 기록 ── */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <Btn variant="outline" style={{ flex: 1, padding: "14px 0", fontSize: 14, fontWeight: 700, borderRadius: 12, justifyContent: "center", borderColor: T.green + "40", color: T.green }}
          onClick={() => onAction?.("add_buy")}>
          <Plus size={16} strokeWidth={2} /> 매수 (추가매수)
        </Btn>
        <Btn variant="outline" style={{ flex: 1, padding: "14px 0", fontSize: 14, fontWeight: 700, borderRadius: 12, justifyContent: "center", borderColor: T.red + "40", color: T.red }}
          onClick={() => onAction?.("partial_sell")}>
          <Minus size={16} strokeWidth={2} /> 매도 (일부매도)
        </Btn>
      </div>

      {/* ── 투자 여정 타임라인 ── */}
      {(() => {
        const jc = holding.journey_cache || {};
        if (!jc.days_since_add && !jc.state_path?.length) return null;
        const path = jc.state_path || [];
        const lt = jc.last_transition;
        return (
          <Card style={{ marginBottom: 20, borderLeft: `3px solid ${T.accent}` }}>
            <SectionTitle icon="Map" tip="매수 이후 상태가 어떻게 변해왔는지 보여줍니다">투자 여정</SectionTitle>
            <div style={{ fontSize: 12, color: T.textDim, marginBottom: 14, lineHeight: 1.5 }}>
              이 종목을 등록한 이후의 흐름이에요. 며칠 됐는지, 지금 상태가 얼마나 유지됐는지, 최고 수익률과 현재 위치를 한눈에 볼 수 있어요.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginBottom: 20 }}>
              <div style={{ padding: "12px", background: T.bg, borderRadius: 10 }}>
                <div className="label-micro" style={{ marginBottom: 6 }}>등록 경과</div>
                <div className="mono-num" style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{jc.days_since_add ?? "-"}일</div>
              </div>
              <div style={{ padding: "12px", background: T.bg, borderRadius: 10 }}>
                <div className="label-micro" style={{ marginBottom: 6 }}>현재 상태 유지</div>
                <div className="mono-num" style={{ fontSize: 20, fontWeight: 700, color: sc.color }}>{jc.state_streak_days ?? "-"}일</div>
              </div>
              <div style={{ padding: "12px", background: T.bg, borderRadius: 10 }}>
                <div className="label-micro" style={{ marginBottom: 6 }}>최고 수익률</div>
                <div className="mono-num" style={{ fontSize: 20, fontWeight: 700, color: (jc.peak_pnl || 0) >= 0 ? T.green : T.red }}>{jc.peak_pnl != null ? `${jc.peak_pnl >= 0 ? "+" : ""}${jc.peak_pnl.toFixed(1)}%` : "-"}</div>
              </div>
              <div style={{ padding: "12px", background: T.bg, borderRadius: 10 }}>
                <div className="label-micro" style={{ marginBottom: 6 }}>고점 대비 하락</div>
                <div className="mono-num" style={{ fontSize: 20, fontWeight: 700, color: (jc.drawdown_from_peak || 0) < 0 ? T.red : T.green }}>{jc.drawdown_from_peak != null ? (jc.drawdown_from_peak === 0 ? "0% (고점)" : `${jc.drawdown_from_peak}%p`) : "-"}</div>
              </div>
            </div>

            {/* 상태 경로 시각화 */}
            {path.length > 1 && (
              <div style={{ marginBottom: 16 }}>
                <div className="label-micro" style={{ marginBottom: 8 }}>상태 변화 경로</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  {path.reduce((acc, s, i) => {
                    if (i === 0 || s !== path[i - 1]) {
                      const count = path.slice(i).findIndex((x, j) => j > 0 && x !== s);
                      const days = count === -1 ? path.length - i : count;
                      acc.push({ state: s, days });
                    }
                    return acc;
                  }, []).map((item, i, arr) => {
                    const st = getStateConfig(item.state);
                    return (
                      <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span className="mono-num" style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, background: st.bg, color: st.color, border: `1px solid ${st.color}30`, fontWeight: 600 }}>{st.label} {item.days}일</span>
                        {i < arr.length - 1 && <ArrowRight size={14} color={T.textMuted} />}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 마지막 전환 이유 */}
            {lt && (
              <div style={{ padding: "12px", background: T.bg, borderRadius: 10, fontSize: 13, color: T.textDim, marginBottom: 16 }}>
                <span className="label-micro" style={{ color: T.text, marginRight: 8 }}>마지막 전환</span>
                <span className="mono-num" style={{ opacity: 0.6, marginRight: 8 }}>({lt.at})</span>
                {(getStateConfig(lt.from)).label || lt.from} <ArrowRight size={12} style={{ display: "inline", verticalAlign: "middle", margin: "0 4px" }} /> {(getStateConfig(lt.to)).label || lt.to}
                {lt.why_top3?.length > 0 && (
                  <div style={{ marginTop: 8, color: T.textSub || T.textDim }}>
                    원인: {lt.why_top3.map(w =>
                      String(w)
                        .replace(/\bRisk\b/g, "위험도")
                        .replace(/\bSupport\b/g, "지지도")
                    ).join(" · ")}
                  </div>
                )}
              </div>
            )}

            {/* 전일 대비 변화 */}
            <div style={{ padding: "14px", background: T.bg, borderRadius: 10, border: `1px solid ${T.border}` }}>
              <div className="label-micro" style={{ marginBottom: 4 }}>전일 대비 변화</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
                어제와 비교해서 오늘 위험도·지지도·수익률이 얼마나 달라졌는지 보여줘요.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, color: T.textDim, fontWeight: 500 }} title="가격 변동성·하락폭 등 종목의 부담 요인을 0~100으로 나타낸 값. 높을수록 부담이 큰 상태">위험도</div>
                  <div className="mono-num" style={{ fontSize: 18, fontWeight: 700, color: (jc.risk_delta_1d || 0) > 0 ? T.red : (jc.risk_delta_1d || 0) < 0 ? T.green : T.textDim }}>
                    {jc.risk_delta_1d != null && jc.risk_delta_1d !== 0 ? `${jc.risk_delta_1d > 0 ? "+" : ""}${jc.risk_delta_1d}` : "-"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: T.textDim, fontWeight: 500 }} title="가격 안정성·수급 우호도 등 종목의 받침 요인을 0~100으로 나타낸 값. 높을수록 받침이 강한 상태">지지도</div>
                  <div className="mono-num" style={{ fontSize: 18, fontWeight: 700, color: (jc.support_delta_1d || 0) > 0 ? T.green : (jc.support_delta_1d || 0) < 0 ? T.red : T.textDim }}>
                    {jc.support_delta_1d != null && jc.support_delta_1d !== 0 ? `${jc.support_delta_1d > 0 ? "+" : ""}${jc.support_delta_1d}` : "-"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: T.textDim, fontWeight: 500 }}>수익률</div>
                  <div className="mono-num" style={{ fontSize: 18, fontWeight: 700, color: (jc.current_pnl || 0) >= 0 ? T.green : T.red }}>
                    {jc.current_pnl != null ? `${jc.current_pnl >= 0 ? "+" : ""}${jc.current_pnl.toFixed(1)}%` : "-"}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* ── 불안 패턴 감지 (AI 케어) ── */}
      {anxietyLevel && canAccess(plan, "ai_care") && (
        <Card style={{ marginBottom: 20, borderLeft: `3px solid ${T.accent}`, background: T.accent + "08" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "start" }}>
            <HeartPulse size={24} color={T.accent} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.accent }}>Pause 케어</span>
                <span className="label-micro" style={{ background: T.accent + "20", color: T.accent, padding: "2px 6px", borderRadius: 4 }}>CORE</span>
              </div>
              {anxietyLevel === "high" ? (
                <div style={{ fontSize: 14, color: T.text, lineHeight: 1.7 }}>
                  최근 7일간 {holding.name} 종목을 <strong style={{ color: T.accent }}>5회 이상</strong> 확인하셨습니다.
                  확인을 자주 하게 되는 구간이지만, 감정적인 결정을 하기 쉬운 시점이기도 합니다.
                  <br /><br />
                  <span style={{ opacity: 0.8 }}>데이터 상태가 변하지 않았다면, 다음 리포트까지 기다려보는 것도 도움이 됩니다.</span>
                </div>
              ) : (
                <div style={{ fontSize: 14, color: T.text, lineHeight: 1.7 }}>
                  최근 {holding.name}에 대해 자주 확인하고 계십니다.
                  <strong>상태 변화가 있을 때만 확인해도 충분합니다.</strong> 판단 일관성 유지에 도움이 됩니다.
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ── 데이터 변화 요약 (Sprint 20: 판단 근거 → 사실 요약) ── */}
      <Card style={{ marginBottom: 20 }}>
        <SectionTitle icon="CheckCircle2" tip="최근 데이터에서 감지된 주요 변화를 정리한 내용입니다">데이터 변화 요약</SectionTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(report.reasons || []).slice(0, 3).map((r, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 14, color: T.text, lineHeight: 1.5 }}>
              <ChevronRight size={16} color={T.accent} style={{ flexShrink: 0, marginTop: 3 }} />
              <span>{r}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* ── 조건부 시나리오 ── */}
      {p.conditions?.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle icon="HelpCircle" tip="이런 데이터 변화가 발생하면 상태 태그가 달라질 수 있습니다">데이터 변화 시나리오</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {p.conditions.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: T.yellow, lineHeight: 1.5 }}>
                <Target size={14} color={T.yellow} style={{ flexShrink: 0, marginTop: 3 }} />
                <span>{c}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── 체크포인트 ── */}
      <Card style={{ marginBottom: 20 }}>
        <SectionTitle icon="CheckCircle2" tip="다음 리포트까지 확인하면 좋을 데이터 포인트입니다">다음 확인 포인트</SectionTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(report.checkpoints || []).slice(0, 1).map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: T.textDim, lineHeight: 1.5 }}>
              <CheckCircle2 size={14} color={T.textDim} style={{ flexShrink: 0, marginTop: 3 }} />
              <span>{c}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* ── 알림 설정 (Pro) ── */}
      {canAccess(plan, "alert_setting") ? (
        <Card style={{ marginTop: 20 }}>
          <SectionTitle icon="Bell" tip="특정 조건이 충족되면 Pause가 알려줍니다" badge="CORE">알림 설정</SectionTitle>
          <AlertSetter symbol={holding.symbol} name={holding.name} token={token} />
        </Card>
      ) : (
        <Card style={{ marginTop: 20, cursor: "pointer", display: "flex", alignItems: "center", gap: 16 }} onClick={() => onUpgrade?.("alert_setting")}>
          <div style={{ padding: 12, background: T.bg, borderRadius: 12 }}><Lock size={20} color={T.textMuted} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>조건부 알림 설정</div>
            <div style={{ fontSize: 12, color: T.textDim }}>Core 플랜에서 조건부 알림을 설정할 수 있습니다</div>
          </div>
          <span className="label-micro" style={{ color: T.accent }}>CORE</span>
        </Card>
      )}
    </div>
  );
}
